import type { Role } from "./types";

export type ModuleKey =
  | "pos_register"
  | "catalog_inventory"
  | "receiving"
  | "customers"
  | "sales_returns"
  | "settings_security"
  | "basic_reporting"
  | "labels"
  | "loyalty"
  | "storefront"
  | "customer_special_orders"
  | "purchase_orders"
  | "edi_integrations"
  | "supplier_invoices"
  | "supplier_returns"
  | "accounting_exports"
  | "trade_ins"
  | "imports"
  | "advanced_reports";

export type ModuleClassification = "core" | "optional";
export type ModuleCategory = "sales" | "catalog" | "customers" | "purchasing" | "operations" | "integrations" | "reporting" | "settings";
export type ModuleMaturity = "available" | "beta" | "foundation";
export type ModuleAvailability = "available" | "launch_deferred";
export type ModuleRoute =
  | "pos"
  | "inventory"
  | "item"
  | "supplier-search"
  | "receive"
  | "customers"
  | "orders"
  | "returns"
  | "settings"
  | "labels"
  | "loyalty"
  | "storefront-admin"
  | "onboarding"
  | "purchasing"
  | "reports"
  | "trade-ins"
  | "imports"
  | "reconciliation";

export interface ModuleDefinition {
  key: ModuleKey;
  displayName: string;
  description: string;
  category: ModuleCategory;
  classification: ModuleClassification;
  defaultEnabled: boolean;
  dependencies: ModuleKey[];
  conflicts: ModuleKey[];
  requiredRoles: Role[];
  relatedNavigation: string[];
  relatedRoutes: ModuleRoute[];
  relatedApiCapabilities: string[];
  relatedSettingsSections: string[];
  stopsBackgroundWork: boolean;
  configurationRoute?: string;
  maturity: ModuleMaturity;
  availability?: ModuleAvailability;
  unavailableReason?: string;
  displayOrder: number;
  disabledReadPolicy: string;
}

export type OrganizationModuleState = {
  tenantId: string;
  moduleKey: ModuleKey;
  enabled: boolean;
  enabledAt: string | null;
  disabledAt: string | null;
  changedBy: string | null;
  reason: string | null;
  config: Record<string, unknown>;
  version: number;
  updatedAt: string;
};

export type OrganizationModuleView = {
  definition: ModuleDefinition;
  state: OrganizationModuleState;
  dependents: ModuleKey[];
};

export const launchDeferredModuleReason = "Deferred for the initial PaperbackOS launch. The module remains installed and historical data is preserved, but administrators cannot enable it yet.";
export const launchDeferredPurchasingModuleKeys: ModuleKey[] = [
  "customer_special_orders",
  "purchase_orders",
  "edi_integrations",
  "supplier_invoices",
  "supplier_returns",
  "accounting_exports",
];

export const moduleDefinitions: ModuleDefinition[] = [
  {
    key: "pos_register",
    displayName: "Register/POS",
    description: "Counter sales, register sessions, tendering, receipts, and POS customer lookup.",
    category: "sales",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Register"],
    relatedRoutes: ["pos"],
    relatedApiCapabilities: ["pos"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 10,
    disabledReadPolicy: "Core sales functions cannot be disabled at the organization level.",
  },
  {
    key: "catalog_inventory",
    displayName: "Catalog and Inventory",
    description: "Works, editions, variations, stock positions, copy tracking, and item maintenance.",
    category: "catalog",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Inventory"],
    relatedRoutes: ["inventory", "item", "supplier-search"],
    relatedApiCapabilities: ["catalog", "inventory", "supplier_search"],
    relatedSettingsSections: ["catalog", "structure", "suppliers"],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 20,
    disabledReadPolicy: "Core catalog and inventory functions cannot be disabled at the organization level.",
  },
  {
    key: "receiving",
    displayName: "Basic Receiving",
    description: "Manual stock receiving, batch review, barcode generation, and inventory movements.",
    category: "operations",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Add Stock"],
    relatedRoutes: ["receive"],
    relatedApiCapabilities: ["receiving"],
    relatedSettingsSections: ["operations"],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 30,
    disabledReadPolicy: "Basic receiving cannot be disabled because inventory integrity depends on it.",
  },
  {
    key: "customers",
    displayName: "Customers",
    description: "Customer profiles, communication preferences, requests, and account lookup.",
    category: "customers",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Requests"],
    relatedRoutes: ["customers"],
    relatedApiCapabilities: ["customers"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 40,
    disabledReadPolicy: "Customer records remain core because sales, returns, and audit history depend on them.",
  },
  {
    key: "sales_returns",
    displayName: "Sales History and Returns",
    description: "Order management, sale history, customer returns, exchanges, store credit, and reconciliation.",
    category: "sales",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Sales & Returns"],
    relatedRoutes: ["orders", "returns", "reconciliation"],
    relatedApiCapabilities: ["orders", "returns", "reconciliation", "store_credit"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 50,
    disabledReadPolicy: "Historical sales and returns remain core so financial records continue to render.",
  },
  {
    key: "labels",
    displayName: "Labels",
    description: "Inventory label templates, label queues, previews, and print generation.",
    category: "operations",
    classification: "core",
    defaultEnabled: true,
    dependencies: ["catalog_inventory"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Labels"],
    relatedRoutes: ["labels"],
    relatedApiCapabilities: ["labels"],
    relatedSettingsSections: ["labels"],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 60,
    disabledReadPolicy: "Labeling is part of core inventory operations and cannot be disabled separately in this phase.",
  },
  {
    key: "basic_reporting",
    displayName: "Basic Reporting",
    description: "Required operational reporting and source-record drill-ins.",
    category: "reporting",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["basic_reports"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 70,
    disabledReadPolicy: "Basic reporting remains available for operational and audit needs.",
  },
  {
    key: "settings_security",
    displayName: "Users, Roles, Settings, and Security",
    description: "Administrator settings, organization profile, authentication, authorization, subscriptions, and audit logs.",
    category: "settings",
    classification: "core",
    defaultEnabled: true,
    dependencies: [],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: ["Settings"],
    relatedRoutes: ["settings", "onboarding"],
    relatedApiCapabilities: ["settings", "auth", "audit", "subscription", "onboarding"],
    relatedSettingsSections: ["profile", "appearance", "structure", "catalog", "operations"],
    stopsBackgroundWork: false,
    configurationRoute: "/app/settings",
    maturity: "available",
    displayOrder: 80,
    disabledReadPolicy: "Settings, authentication, auditing, and security cannot be disabled.",
  },
  {
    key: "loyalty",
    displayName: "Loyalty",
    description: "Customer enrollments, points earning, rewards, campaigns, redemption, and point expiration.",
    category: "customers",
    classification: "optional",
    defaultEnabled: true,
    dependencies: ["customers", "pos_register"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: [],
    relatedRoutes: ["loyalty"],
    relatedApiCapabilities: ["loyalty"],
    relatedSettingsSections: ["loyalty"],
    stopsBackgroundWork: true,
    configurationRoute: "/app/settings/loyalty",
    maturity: "available",
    displayOrder: 100,
    disabledReadPolicy: "Balances and ledger history stay readable; earning, redemption, enrollment, campaigns, adjustments, and expirations are blocked.",
  },
  {
    key: "storefront",
    displayName: "Ecommerce Storefront",
    description: "Public catalog browsing, carts, reservations, checkout, account access, collections, staff picks, and custom domains.",
    category: "sales",
    classification: "optional",
    defaultEnabled: true,
    dependencies: ["catalog_inventory", "customers", "sales_returns"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: [],
    relatedRoutes: ["storefront-admin"],
    relatedApiCapabilities: ["storefront"],
    relatedSettingsSections: ["storefront"],
    stopsBackgroundWork: true,
    configurationRoute: "/app/settings/storefront",
    maturity: "available",
    displayOrder: 110,
    disabledReadPolicy: "Admin configuration and historical orders stay readable; public shopping, cart changes, checkout, reservations, wishlist writes, and storefront publishing changes are blocked.",
  },
  {
    key: "customer_special_orders",
    displayName: "Customer Special Orders",
    description: "Customer requested titles, sourcing, deposits, special-order status, and purchase-order allocations.",
    category: "customers",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["customers"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["customer_special_orders"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 120,
    disabledReadPolicy: "Existing customer requests remain readable; creating or updating special orders and purchase allocations is blocked.",
  },
  {
    key: "purchase_orders",
    displayName: "Purchase Orders",
    description: "Vendors, vendor products, replenishment reviews, purchase orders, approvals, acknowledgments, and PO receiving.",
    category: "purchasing",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["receiving"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Purchasing"],
    relatedRoutes: ["purchasing"],
    relatedApiCapabilities: ["purchase_orders", "vendors", "replenishment", "po_receiving"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 130,
    disabledReadPolicy: "Historical vendors, purchase orders, shipments, receiving sessions, invoices, and returns stay readable; new purchasing activity and PO state changes are blocked.",
  },
  {
    key: "edi_integrations",
    displayName: "EDI and Provider Integrations",
    description: "Electronic purchase-order transmission, provider setup, trading partners, inbound messages, polling, retries, and simulated responses.",
    category: "integrations",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["purchase_orders"],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["edi", "integrations"],
    relatedSettingsSections: [],
    stopsBackgroundWork: true,
    maturity: "foundation",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 140,
    disabledReadPolicy: "Messages, health, providers, and trading-partner records stay readable; transmissions, inbound processing, retries, credentials, simulated responses, and queue processing are blocked.",
  },
  {
    key: "supplier_invoices",
    displayName: "Supplier Invoices",
    description: "Inbound supplier invoices, PO matching, discrepancy resolution, credit memos, and invoice state transitions.",
    category: "purchasing",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["purchase_orders"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["supplier_invoices"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 150,
    disabledReadPolicy: "Historical invoices, discrepancies, and credit memos stay readable; inbound invoices, matching, state changes, discrepancy resolution, and new credit memos are blocked.",
  },
  {
    key: "supplier_returns",
    displayName: "Supplier Returns",
    description: "Supplier return eligibility, suggestions, authorizations, staging, shipping, credit reconciliation, and return history.",
    category: "purchasing",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["purchase_orders", "receiving"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["supplier_returns"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 160,
    disabledReadPolicy: "Existing returns, cartons, credits, and history stay readable; eligibility calculations, new returns, authorization updates, staging, shipping, cancellation, and credit reconciliation are blocked.",
  },
  {
    key: "accounting_exports",
    displayName: "Accounting Exports",
    description: "Supplier-invoice and credit-memo export batches for accounting handoff.",
    category: "purchasing",
    classification: "optional",
    defaultEnabled: false,
    dependencies: ["supplier_invoices"],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: [],
    relatedRoutes: [],
    relatedApiCapabilities: ["accounting_exports"],
    relatedSettingsSections: [],
    stopsBackgroundWork: true,
    maturity: "foundation",
    availability: "launch_deferred",
    unavailableReason: launchDeferredModuleReason,
    displayOrder: 170,
    disabledReadPolicy: "Prior export batches remain readable; creating new export batches is blocked.",
  },
  {
    key: "trade_ins",
    displayName: "Trade-ins",
    description: "Trade-in quotes, inspection workflow, inventory acquisition, store-credit issuance, reversals, and trade-in policy.",
    category: "operations",
    classification: "optional",
    defaultEnabled: true,
    dependencies: ["customers", "catalog_inventory", "sales_returns"],
    conflicts: [],
    requiredRoles: ["administrator", "manager", "staff"],
    relatedNavigation: ["Trade-ins"],
    relatedRoutes: ["trade-ins"],
    relatedApiCapabilities: ["trade_ins"],
    relatedSettingsSections: ["tradeIns"],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 180,
    disabledReadPolicy: "Trade-in drafts, issued credit, and history stay readable; quotes, completion, transitions, acquisition, credit issuance, reversals, and policy updates are blocked.",
  },
  {
    key: "imports",
    displayName: "Inventory Imports",
    description: "CSV/API inventory import jobs, previews, conflict resolution, application, reversals, and artifacts.",
    category: "operations",
    classification: "optional",
    defaultEnabled: true,
    dependencies: ["catalog_inventory"],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: ["Imports"],
    relatedRoutes: ["imports"],
    relatedApiCapabilities: ["imports"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 190,
    disabledReadPolicy: "Existing import jobs and artifacts stay readable; creating, previewing, applying, resolving, reversing, or exporting import jobs is blocked.",
  },
  {
    key: "advanced_reports",
    displayName: "Advanced Reports",
    description: "Full reporting workspace, advanced filters, CSV exports, and sales-source drill-ins.",
    category: "reporting",
    classification: "optional",
    defaultEnabled: true,
    dependencies: ["basic_reporting"],
    conflicts: [],
    requiredRoles: ["administrator", "manager"],
    relatedNavigation: ["Reports"],
    relatedRoutes: ["reports"],
    relatedApiCapabilities: ["advanced_reports"],
    relatedSettingsSections: [],
    stopsBackgroundWork: false,
    maturity: "available",
    displayOrder: 200,
    disabledReadPolicy: "Required basic reporting remains available; the advanced reports workspace, filters, drill-ins, and exports are hidden and blocked.",
  },
] satisfies ModuleDefinition[];

export const moduleDefinitionsByKey = Object.fromEntries(moduleDefinitions.map((definition) => [definition.key, definition])) as unknown as Record<ModuleKey, ModuleDefinition>;
export const moduleKeys = moduleDefinitions.map((definition) => definition.key) as ModuleKey[];
export const coreModuleKeys = moduleDefinitions.filter((definition) => definition.classification === "core").map((definition) => definition.key);
export const optionalModuleKeys = moduleDefinitions.filter((definition) => definition.classification === "optional").map((definition) => definition.key);
export const launchDeferredModuleKeys = moduleDefinitions.filter((definition) => definition.availability === "launch_deferred").map((definition) => definition.key);

export function isModuleKey(value: string): value is ModuleKey {
  return Object.prototype.hasOwnProperty.call(moduleDefinitionsByKey, value);
}

export function getModuleDefinition(key: ModuleKey): ModuleDefinition {
  return moduleDefinitionsByKey[key];
}

export function isModuleLaunchDeferred(key: ModuleKey): boolean {
  return moduleDefinitionsByKey[key].availability === "launch_deferred";
}

export function moduleForRoute(route: ModuleRoute): ModuleDefinition | undefined {
  return moduleDefinitions.find((definition) => definition.relatedRoutes.includes(route));
}

export function moduleForNavigation(label: string): ModuleDefinition | undefined {
  return moduleDefinitions.find((definition) => definition.relatedNavigation.includes(label));
}

export function dependentsForModule(key: ModuleKey): ModuleKey[] {
  return moduleDefinitions.filter((definition) => definition.dependencies.includes(key)).map((definition) => definition.key);
}

export function isModuleEnabledInMap(states: Partial<Record<ModuleKey, boolean>>, key: ModuleKey): boolean {
  const definition = moduleDefinitionsByKey[key];
  if (definition.classification === "core") return true;
  return states[key] ?? definition.defaultEnabled;
}
